/**
 * Maps semantic app icon names to FontAwesome icon names.
 *
 * Consumers use the keys (e.g. `"home"`, `"message"`); the Icon component
 * resolves them to the corresponding FA identifier at render time.
 */

export const APP_ICON_NAMES = {
  home: 'house',
  message: 'message',
  reply: 'reply',
  /** Open / reply in a message thread. */
  thread: 'messages',
  users: 'users',
  settings: 'gear',
  info: 'circle-info',
  download: 'download',
  copy: 'copy',
  /** Copy entire text field (e.g. composer). */
  copyAll: 'files',
  /** Mention a user (@). */
  at: 'at',
  /** Text field “select all” (e.g. composer context menu). */
  selectAll: 'border-all',
  link: 'link',
  externalLink: 'arrow-up-right-from-square',
  /** Upload / attach (image, video, files). */
  upload: 'arrow-up-from-bracket',
  film: 'film',
  logout: 'right-from-bracket',
  shield: 'shield-check',
  key: 'key',
  bell: 'bell',
  search: 'magnifying-glass',
  user: 'user',
  palette: 'palette',
  lock: 'lock',
  mask: 'mask',
  plus: 'plus',
  check: 'check',
  clock: 'clock',
  timer: 'timer',
  x: 'xmark',
  spaces: 'grid-2',
  ellipsis: 'ellipsis',
  ellipsisVertical: 'ellipsis-vertical',
  smile: 'face-smile',
  smilePlus: 'face-smile-plus',
  send: 'paper-plane',
  trash: 'trash',
  fileExport: 'file-export',
  fileImport: 'file-import',
  chevronDown: 'chevron-down',
  chevronRight: 'chevron-right',
  chevronUp: 'chevron-up',
  camera: 'camera',
  image: 'image',
  globe: 'globe',
  /** FA alias of earth-americas */
  earth: 'earth-americas',
  desktop: 'desktop',
  server: 'server',
  bars: 'bars',
  eye: 'eye',
  eyeSlash: 'eye-slash',
  thumbsUp: 'thumbs-up',
  warning: 'triangle-exclamation',
  success: 'circle-check',
  error: 'circle-xmark',
  badgeCheck: 'badge-check',
  fileArrowDown: 'file-arrow-down',
  fileArrowUp: 'file-arrow-up',
  arrowLeft: 'arrow-left',
  arrowRight: 'arrow-right',
  /** Conversations + Spaces unified sidebar view */
  swap: 'arrow-left-arrow-right',
  circle: 'circle',
  pen: 'pen',
  noteSticky: 'note-sticky',
  trophy: 'trophy',
  userPlus: 'user-plus',
  group: 'user-group',
  device: 'mobile-screen-button',
  heart: 'heart',
  ban: 'ban',
  gavel: 'gavel',
  fire: 'fire',
  userMinus: 'user-minus',
  archive: 'box-archive',
  star: 'star',
  filter: 'filter',
  sort: 'arrow-up-arrow-down',
  sortAlpha: 'arrow-down-a-z',
  pin: 'thumbtack',
  focusFrame: 'arrows-to-eye',
  locationPin: 'location-pin',
  rotateRight: 'arrow-rotate-right',
  rotateLeft: 'arrow-rotate-left',
  handshake: 'handshake',
  personPregnant: 'person-pregnant',
  creditCard: 'credit-card',
  coconut: 'coconut',
  kidneys: 'kidneys',
  rabbit: 'rabbit',
  /** FA Pro has no faModem; router is the canonical networking-hardware glyph. */
  modem: 'router',
  crown: 'crown',
  handFist: 'hand-fist',
  userSecret: 'user-secret',
  hatWizard: 'hat-wizard',
  moon: 'moon',
  hourglassHalf: 'hourglass-half',
  explosion: 'explosion',
  doorOpen: 'door-open',
  islandTropical: 'island-tropical',
  gift: 'gift',
  suitcase: 'suitcase',
  landmark: 'landmark',
  mugSaucer: 'mug-saucer',
  parachuteBox: 'parachute-box',
  briefcase: 'briefcase',
  pizzaSlice: 'pizza-slice',
  award: 'award',
  handcuffs: 'handcuffs',
  hatChef: 'hat-chef',
  temperatureFull: 'temperature-full',
  snowflake: 'snowflake',
  dragon: 'dragon',
  skull: 'skull',
  wineGlass: 'wine-glass',
  utensils: 'utensils',
  lamp: 'lamp',
  boxBallot: 'box-ballot',
  ballotCheck: 'ballot-check',
  tooth: 'tooth',
  bottleWater: 'bottle-water',
  crab: 'crab',
  leaf: 'leaf',
  phone: 'phone',
  phoneHangup: 'phone-hangup',
  video: 'video',
  videoSlash: 'video-slash',
  microphone: 'microphone',
  microphoneSlash: 'microphone-slash',
  volumeHigh: 'volume-high',
  volumeMute: 'volume-slash',
  musicalWaveform: 'waveform',
  screenShare: 'screen-users',
  phoneIncoming: 'phone-arrow-down-left',
  expand: 'maximize',
  compress: 'compress',
  signalBars: 'signal-bars',
  signalBarsGood: 'signal-bars-good',
  signalBarsFair: 'signal-bars-fair',
  signalBarsWeak: 'signal-bars-weak',
  cake: 'cake-candles',
  envelope: 'envelope',
  folder: 'folder',
  folders: 'folders',
  layerGroup: 'layer-group',
  ballPile: 'ball-pile',
  building: 'building',
  family: 'family',
  sportsball: 'sportsball',
  dice: 'dice',
  diceD10: 'dice-d10',
  diceD12: 'dice-d12',
  gameboard: 'game-board',
  gameConsoleHandheld: 'game-console-handheld',
} as const;

export type AppIconName = keyof typeof APP_ICON_NAMES;

export function isAppIconName(value: string): value is AppIconName {
  return Object.prototype.hasOwnProperty.call(APP_ICON_NAMES, value);
}
